// Chained one-time migration of the theme localStorage key across rebrands.
// Current canonical key: `caladan.theme` (rebrand date 2026-04-24).
// Legacy keys, in chronological order: `natgas.theme` → `arrakis.theme` (2026-04-19)
// → `caladan.theme` (2026-04-24). Safe to drop this helper once all active
// users have been promoted past `caladan.theme` (est. 3 months from the latest
// rebrand based on session frequency).
//
// Extracted to a pure helper so it can be unit-tested without a DOM test
// framework. Takes the storage object as a parameter (defaults to window's
// localStorage when available) so tests can pass a stub.
//
// Idempotent: once the canonical key is set, legacy keys are removed so
// subsequent mounts are no-ops. If the canonical key is already set, legacy
// keys are dropped without clobbering.

const CANONICAL_KEY = 'caladan.theme';
const LEGACY_KEYS = ['arrakis.theme', 'natgas.theme'];

/**
 * @param {{ getItem: (k: string) => string | null,
 *          setItem: (k: string, v: string) => void,
 *          removeItem: (k: string) => void }} [storage]
 * @returns {'dark' | 'light' | null} the resolved theme after migration, or
 *          null if no preference is stored.
 */
export function migrateThemeKey(storage) {
  const store = storage !== undefined ? storage : resolveDefaultStorage();
  if (!store) return null;

  try {
    for (const legacy of LEGACY_KEYS) {
      const val = store.getItem(legacy);
      if (val == null) continue;
      // Only promote the most recent legacy value — once the canonical key
      // is set (either by an earlier mount or by an earlier iteration of
      // this loop, which processes keys newest→oldest), don't clobber it.
      if (store.getItem(CANONICAL_KEY) == null) {
        store.setItem(CANONICAL_KEY, val);
      }
      store.removeItem(legacy);
    }
    const val = store.getItem(CANONICAL_KEY);
    return val === 'dark' || val === 'light' ? val : null;
  } catch {
    // Storage unavailable (private mode, SSR, disabled cookies, etc.)
    return null;
  }
}

function resolveDefaultStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
